/* ==========================================================================
   Cart Rewards - shared core runtime
   --------------------------------------------------------------------------
   Loaded by both cart-rewards.js (Rewards Ladder) and cart-tier.js (Bundle
   Tier). Either section can be removed independently: whichever remains still
   loads this file, and this file does nothing on its own.

   Provides:
     CartRewardsCore.bus        cart state + mutation events (debounced 300ms)
     CartRewardsCore.money      currency detection + Intl formatting
     CartRewardsCore.thresholds per-market parsing + Zonos accessor chain
     CartRewardsCore.mounts     clones a section template into the cart drawer
                                and/or cart page, and re-mounts when the theme
                                re-renders those regions
     CartRewardsCore.track      GA4 / Klaviyo / Meta pixel fan-out

   Supawear build. One deliberate change from the 2EROS original: the theme
   editor now reads the REAL cart instead of skipping the fetch, and cart
   MUTATIONS are blocked instead. Previously the whole runtime went idle in
   design mode, which is what made the progress bar show preview numbers next
   to a real cart. Blocking writes rather than reads fixes that at the root
   for both features at once.

   Debug: append ?rewards_debug=1 to any URL.
   ========================================================================== */
(function () {
  'use strict';

  if (window.CartRewardsCore) return;

  var DEBUG = /[?&]rewards_debug=1/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    var a = ['%c[cart-rewards]', 'color:#B5FFCA;font-weight:600'];
    console.log.apply(console, a.concat([].slice.call(arguments)));
  }
  function warn() {
    var a = ['[cart-rewards]'];
    if (DEBUG) console.warn.apply(console, a.concat([].slice.call(arguments)));
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function q(sel, root) {
    if (!sel) return null;
    try {
      return (root || document).querySelector(sel);
    } catch (e) {
      return null;
    }
  }
  function qFirst(list, root) {
    for (var i = 0; i < list.length; i++) {
      var el = q(list[i], root);
      if (el) return el;
    }
    return null;
  }
  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }
  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }
  /* esc() output is only ever inserted as element text content, never into an
     attribute, so escaping the three markup-significant characters is enough. */
  var ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>]/g, function (c) {
      return ESC_MAP[c];
    });
  }

  /* ======================================================================
     MONEY
     ====================================================================== */

  var ZERO_DECIMAL = [
    'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG',
    'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
  ];

  var money = {
    info: function () {
      var S = window.Shopify || {};
      var c = S.currency || {};
      return {
        active: c.active || (document.documentElement.getAttribute('data-currency') || 'AUD'),
        rate: num(c.rate) || 1
      };
    },

    /* Shopify's AJAX API always expresses money in 1/100 units, including
       zero-decimal currencies, so /100 is correct everywhere. */
    format: function (minor, currency, locale) {
      var cur = currency || money.info().active;
      var amount = (num(minor) || 0) / 100;
      var zero = ZERO_DECIMAL.indexOf(cur) !== -1;
      var whole = Math.abs(amount % 1) < 0.005;
      var opts = {
        style: 'currency',
        currency: cur,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: zero || whole ? 0 : 2,
        maximumFractionDigits: zero ? 0 : 2
      };
      var loc = locale || document.documentElement.lang || 'en';
      try {
        return new Intl.NumberFormat(loc, opts).format(amount);
      } catch (e) {
        /* narrowSymbol is unsupported on older Safari */
        try {
          delete opts.currencyDisplay;
          return new Intl.NumberFormat(loc, opts).format(amount);
        } catch (e2) {
          return (zero ? Math.round(amount) : amount.toFixed(2)) + ' ' + cur;
        }
      }
    },

    /* major units in shop base currency -> minor units in active currency */
    toActiveMinor: function (baseMajor) {
      var n = num(baseMajor);
      if (n === null) return null;
      return Math.round(n * money.info().rate * 100);
    },

    /* major units already in the active currency -> minor units */
    majorToMinor: function (major) {
      var n = num(major);
      if (n === null) return null;
      return Math.round(n * 100);
    }
  };

  /* ======================================================================
     THRESHOLDS  (per-market parsing + Zonos)
     ====================================================================== */

  var thresholds = {
    /* "AU:150, US:100\nGB:80" -> { AU:150, US:100, GB:80 } */
    parseMarketMap: function (raw) {
      var out = {};
      if (!raw) return out;
      String(raw)
        .split(/[\n,;]+/)
        .forEach(function (pair) {
          var m = pair.match(/([A-Za-z]{2,3})\s*[:=]\s*([0-9]+(?:\.[0-9]+)?)/);
          if (m) out[m[1].toUpperCase()] = parseFloat(m[2]);
        });
      return out;
    },

    /* Accepts a number, "150", or JSON such as {"AU":150,"US":100} */
    parseMetafield: function (raw, market) {
      if (raw === null || raw === undefined || raw === '') return null;
      if (typeof raw === 'number') return raw;
      var str = String(raw).trim();
      if (str.charAt(0) === '{' || str.charAt(0) === '[') {
        try {
          var parsed = JSON.parse(str);
          if (Array.isArray(parsed)) return num(parsed[0]);
          if (market && parsed[market] != null) return num(parsed[market]);
          var keys = Object.keys(parsed);
          return keys.length ? num(parsed[keys[0]]) : null;
        } catch (e) {
          return null;
        }
      }
      return num(str);
    },

    /* Zonos accessor chain. Returns major units in the active currency. */
    zonos: function (market) {
      var attempts = [
        ['window.zonos.getFreeShippingThreshold(market)', function () {
          return window.zonos && typeof window.zonos.getFreeShippingThreshold === 'function'
            ? window.zonos.getFreeShippingThreshold(market)
            : null;
        }],
        ['window.Zonos.checkout.freeShippingThreshold', function () {
          return window.Zonos && window.Zonos.checkout ? window.Zonos.checkout.freeShippingThreshold : null;
        }],
        ['window.zonos.freeShippingThreshold', function () {
          return window.zonos ? window.zonos.freeShippingThreshold : null;
        }],
        ['window.zonos.settings.freeShippingThreshold', function () {
          return window.zonos && window.zonos.settings ? window.zonos.settings.freeShippingThreshold : null;
        }],
        ['window.Zonos.getSettings().freeShipping.threshold', function () {
          if (!window.Zonos || typeof window.Zonos.getSettings !== 'function') return null;
          var s = window.Zonos.getSettings() || {};
          return (s.freeShipping && s.freeShipping.threshold) || s.freeShippingThreshold || null;
        }],
        ['[data-zonos-free-shipping-threshold]', function () {
          var el = q('[data-zonos-free-shipping-threshold]');
          return el ? el.getAttribute('data-zonos-free-shipping-threshold') : null;
        }],
        ['window.__ZONOS_STATE__.freeShippingThreshold', function () {
          return window.__ZONOS_STATE__ ? window.__ZONOS_STATE__.freeShippingThreshold : null;
        }]
      ];

      for (var i = 0; i < attempts.length; i++) {
        var value = null;
        try {
          value = attempts[i][1]();
        } catch (e) {
          value = null;
        }
        var n = num(value && value.amount != null ? value.amount : value);
        if (n !== null && n > 0) {
          log('Zonos threshold resolved via ' + attempts[i][0] + ' ->', n, '(market ' + market + ')');
          return n;
        }
      }
      warn(
        'Zonos threshold not resolved. Tried: ' +
          attempts.map(function (a) { return a[0]; }).join(' | ')
      );
      return null;
    },

    /* Zonos loads asynchronously - poll briefly, then give up. */
    watchZonos: function (market, onFound) {
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        var v = thresholds.zonos(market);
        if (v !== null) {
          clearInterval(timer);
          onFound(v);
        } else if (tries >= 12) {
          clearInterval(timer);
          log('Zonos polling gave up after ' + tries + ' attempts');
        }
      }, 500);
    }
  };

  /* ======================================================================
     CART BUS
     ====================================================================== */

  var bus = {
    cart: null,
    _subs: [],
    _timer: null,
    _inflight: null,

    subscribe: function (fn) {
      if (this._subs.indexOf(fn) === -1) this._subs.push(fn);
      if (this.cart) {
        try { fn(this.cart); } catch (e) { warn('subscriber error', e); }
      }
      return function () { bus.unsubscribe(fn); };
    },

    unsubscribe: function (fn) {
      var i = this._subs.indexOf(fn);
      if (i !== -1) this._subs.splice(i, 1);
    },

    _emit: function () {
      var cart = this.cart;
      this._subs.slice().forEach(function (fn) {
        try { fn(cart); } catch (e) { warn('subscriber error', e); }
      });
    },

    /* debounced 300ms per spec */
    schedule: function (delay) {
      clearTimeout(this._timer);
      this._timer = setTimeout(function () { bus.refresh(); }, delay == null ? 300 : delay);
    },

    setCart: function (cart) {
      if (!cart || typeof cart.item_count === 'undefined') return;
      this.cart = cart;
      log('cart', cart.item_count + ' items', cart.total_price);
      this._emit();
    },

    refresh: function () {
      if (this._inflight) return this._inflight;
      this._inflight = fetchWithRetry(
        root() + 'cart.js',
        { headers: { Accept: 'application/json' }, credentials: 'same-origin' },
        2
      )
        .then(function (cart) {
          bus._inflight = null;
          bus.setCart(cart);
          return cart;
        })
        .catch(function (err) {
          bus._inflight = null;
          warn('cart fetch failed after retries', err);
          bus._subs.slice().forEach(function (fn) {
            try { fn(null); } catch (e) {}
          });
          return null;
        });
      return this._inflight;
    },

    add: function (items) {
      return post('/cart/add.js', { items: items });
    },

    changeById: function (id, quantity) {
      return post('/cart/change.js', { id: String(id), quantity: quantity });
    }
  };

  function root() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  /* ---- keep the theme's own cart markup in step ----------------------
     Adding a gift with /cart/add.js updates Shopify's cart but leaves the
     theme's server-rendered drawer showing stale line items until the next
     page load. Dawn-family themes - which Edge is one of - expose
     getSectionsToRender() and getSectionInnerHTML() on <cart-drawer>, so we
     ask the Section Rendering API for the same sections the theme would
     request and swap them in.
     Deliberately NOT calling the theme's renderContents(), because on this
     theme that ends with this.open() - a gift add should never yank the
     drawer open. */

  function themeRenderTargets() {
    var targets = [];
    var drawer = q('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      try {
        targets = drawer.getSectionsToRender().slice();
      } catch (e) {
        targets = [];
      }
    }
    if (!targets.length && q('#cart-icon-bubble')) targets.push({ id: 'cart-icon-bubble' });
    if (q('#main-cart-items')) targets.push({ id: 'main-cart-items', selector: '#main-cart-items' });
    return targets;
  }

  function sectionInnerHTML(html, selector) {
    var drawer = q('cart-drawer');
    if (drawer && typeof drawer.getSectionInnerHTML === 'function') {
      try {
        return drawer.getSectionInnerHTML(html, selector);
      } catch (e) {}
    }
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector || '.shopify-section');
      return el ? el.innerHTML : html;
    } catch (e) {
      return html;
    }
  }

  function renderThemeCart(data) {
    if (!data || !data.sections) return;
    themeRenderTargets().forEach(function (t) {
      var html = data.sections[t.id];
      if (!html) return;
      var el = t.selector ? q(t.selector) : document.getElementById(t.id);
      if (!el) return;
      try {
        el.innerHTML = sectionInnerHTML(html, t.selector);
        log('re-rendered theme section ' + t.id);
      } catch (e) {
        warn('could not re-render theme section ' + t.id, e);
      }
    });
  }

  /* Every cart write funnels through here, so this is the single place that
     has to refuse to run inside the theme editor. */
  function post(path, body) {
    if (isDesignMode()) {
      warn('cart mutation blocked in theme editor: ' + path);
      return Promise.reject(new Error('design-mode'));
    }

    var targets = themeRenderTargets();
    if (targets.length) {
      body.sections = targets
        .map(function (t) { return t.id; })
        .filter(function (id, i, all) { return all.indexOf(id) === i; })
        .join(',');
      body.sections_url = window.location.pathname;
    }
    return fetch(root().replace(/\/$/, '') + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data && data.description ? data.description : 'Cart request failed');
        renderThemeCart(data);
        return data;
      });
    });
  }

  function fetchWithRetry(url, opts, retries) {
    var attempt = 0;
    function go() {
      return fetch(url, opts)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .catch(function (err) {
          if (attempt >= retries) throw err;
          attempt++;
          return new Promise(function (res) {
            setTimeout(res, 300 * Math.pow(2, attempt));
          }).then(go);
        });
    }
    return go();
  }

  /* ---- mutation detection ------------------------------------------- */

  var CART_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;

  (function patchFetch() {
    if (typeof window.fetch !== 'function' || window.fetch.__cartRewards) return;
    var orig = window.fetch;
    var patched = function () {
      var args = arguments;
      var p = orig.apply(this, args);
      try {
        var input = args[0];
        var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        if (CART_RE.test(url)) {
          p.then(function () { bus.schedule(); }).catch(function () {});
        }
      } catch (e) {}
      return p;
    };
    patched.__cartRewards = true;
    window.fetch = patched;
  })();

  (function patchXHR() {
    if (!window.XMLHttpRequest || XMLHttpRequest.prototype.__cartRewards) return;
    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (CART_RE.test(String(url))) {
          this.addEventListener('loadend', function () { bus.schedule(); });
        }
      } catch (e) {}
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.__cartRewards = true;
  })();

  ['cart:updated', 'cart:refresh', 'cart-update', 'cart:change', 'ajaxProduct:added'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      var detail = e && e.detail;
      var cart = detail && (detail.cart || (detail.item_count !== undefined ? detail : null));
      if (cart) bus.setCart(cart);
      else bus.schedule();
    });
  });

  /* Dawn-style pubsub, used by this theme family */
  if (window.subscribe && window.PUB_SUB_EVENTS) {
    try {
      Object.keys(window.PUB_SUB_EVENTS).forEach(function (k) {
        if (/cart/i.test(k)) window.subscribe(window.PUB_SUB_EVENTS[k], function () { bus.schedule(); });
      });
    } catch (e) {}
  }

  /* ======================================================================
     MOUNT MANAGER
     ====================================================================== */

  var DRAWER_SELECTORS = [
    '.cart-drawer__inner',
    '#CartDrawer .drawer__inner',
    'cart-drawer .drawer__inner',
    '#CartDrawer',
    'cart-drawer'
  ];

  var CART_PAGE_SELECTORS = [
    '#main-cart-items',
    'cart-items',
    '.cart__items',
    '#MainContent form[action$="/cart"]',
    '#MainContent .shopify-section'
  ];

  function isCartPage() {
    return (
      document.body.classList.contains('template-cart') ||
      /\/cart\/?$/.test(window.location.pathname) ||
      !!q('#main-cart-items, cart-items')
    );
  }

  function stackFor(location, custom) {
    var anchor = null;

    if (location === 'drawer') {
      var inner = (custom && q(custom)) || qFirst(DRAWER_SELECTORS);
      if (!inner) return null;
      var ship = inner.querySelector('.cart-shipping');
      var header = inner.querySelector('.cart-drawer__header, .drawer__header, .modal__heading');
      anchor = {
        parent: inner,
        before: ship ? ship.nextSibling : header ? header.nextSibling : inner.firstChild
      };
    } else {
      var target = (custom && q(custom)) || qFirst(CART_PAGE_SELECTORS);
      if (!target) return null;
      anchor = { parent: target.parentNode, before: target };
      if (!anchor.parent) return null;
    }

    var existing = anchor.parent.querySelector(':scope > .cart-rewards-stack');
    if (existing) return existing;

    var stack = document.createElement('div');
    stack.className = 'cart-rewards-stack cart-rewards-stack--' + location;
    stack.setAttribute('data-cart-rewards-stack', location);
    anchor.parent.insertBefore(stack, anchor.before || null);
    return stack;
  }

  function sortStack(stack) {
    var kids = [].slice.call(stack.children);
    kids
      .sort(function (a, b) {
        return (parseInt(a.getAttribute('data-order'), 10) || 0) - (parseInt(b.getAttribute('data-order'), 10) || 0);
      })
      .forEach(function (k) { stack.appendChild(k); });
  }

  /* opts: { key, order, template, config, onMount(rootEl, location) } */
  function createMounts(opts) {
    var api = {
      instances: [],
      roots: function () {
        return api.instances.map(function (i) { return i.root; });
      },
      sync: function () {
        [
          { location: 'drawer', on: opts.config.showInDrawer !== false, custom: opts.config.drawerTarget },
          { location: 'cart-page', on: opts.config.showInCartPage !== false && isCartPage(), custom: opts.config.cartPageTarget }
        ].forEach(function (spec) {
          api.instances = api.instances.filter(function (i) {
            return i.wrap.isConnected;
          });
          if (!spec.on) return;
          var mounted = api.instances.some(function (i) { return i.location === spec.location; });
          if (mounted) return;

          var stack = stackFor(spec.location, spec.custom);
          if (!stack) return;
          if (stack.querySelector('[data-mount-key="' + opts.key + '"]')) return;

          var wrap = document.createElement('div');
          wrap.className = opts.key + '-mount ' + opts.key + '-mount--' + spec.location;
          wrap.setAttribute('data-mount-key', opts.key);
          wrap.setAttribute('data-order', String(opts.order || 10));

          var frag = opts.template.content
            ? opts.template.content.cloneNode(true)
            : document.createRange().createContextualFragment(opts.template.innerHTML);
          wrap.appendChild(frag);
          stack.appendChild(wrap);
          sortStack(stack);

          var rootEl = wrap.firstElementChild;
          var inst = { location: spec.location, wrap: wrap, root: rootEl };
          api.instances.push(inst);
          log('mounted ' + opts.key + ' into ' + spec.location);
          try {
            opts.onMount(rootEl, spec.location);
          } catch (e) {
            warn('mount handler failed', e);
          }
        });
      },
      destroy: function () {
        api.instances.forEach(function (i) {
          if (i.wrap.parentNode) i.wrap.parentNode.removeChild(i.wrap);
        });
        api.instances = [];
        unobserve(api.sync);
      }
    };

    api.sync();
    observe(api.sync);
    return api;
  }

  /* The theme re-renders the drawer wholesale on cart update, which wipes our
     mount. Re-sync on any relevant DOM change, throttled. */
  var observers = [];
  var observerTimer = null;

  function unobserve(fn) {
    var i = observers.indexOf(fn);
    if (i !== -1) observers.splice(i, 1);
  }

  function observe(fn) {
    if (observers.indexOf(fn) === -1) observers.push(fn);
    if (observers.length > 1) return;
    var mo = new MutationObserver(function () {
      clearTimeout(observerTimer);
      observerTimer = setTimeout(function () {
        observers.forEach(function (f) {
          try { f(); } catch (e) { warn('resync failed', e); }
        });
      }, 80);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  /* ======================================================================
     ANALYTICS
     ====================================================================== */

  function track(eventName, payload) {
    var data = payload || {};
    if (isDesignMode()) return;
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: eventName }, data));
      }
    } catch (e) {}
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', klaviyoName(eventName), data]);
      }
    } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', metaName(eventName), data);
      }
    } catch (e) {}
    log('event', eventName, data);
  }

  function klaviyoName(evt) {
    return evt
      .split('_')
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join(' ');
  }
  function metaName(evt) {
    return evt
      .split('_')
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join('');
  }

  /* ======================================================================
     MISC HELPERS
     ====================================================================== */

  function mergeTags(template, map) {
    var out = String(template == null ? '' : template);
    Object.keys(map).forEach(function (k) {
      out = out.split('{' + k + '}').join(map[k] == null ? '' : map[k]);
    });
    return out;
  }

  function isGiftLine(item, poolIds) {
    if (!item) return false;
    var p = item.properties || {};
    if (p._gift === 'true' || p._gift === true || p._gift === 'True') return true;
    if (p._added_by === 'rewards-ladder') return true;
    if (poolIds && poolIds.length && poolIds.indexOf(item.variant_id) !== -1 && item.final_line_price === 0) return true;
    return false;
  }

  /* Effective discount rate Shopify actually applied to a set of lines.
     Used by the Bundle Tier to check its own claim: if the cart is already
     getting an equal or better rate from somewhere else - a sitewide sale, a
     bundle price, a discount code - the tier is not the binding offer and
     must not assert a percentage the customer is not receiving. */
  function appliedRate(discountMinor, originalMinor) {
    var d = num(discountMinor) || 0;
    var o = num(originalMinor) || 0;
    if (o <= 0) return null;
    return clamp(d / o, 0, 1);
  }

  function liveAnnounce(el, message) {
    if (!el) return;
    clearTimeout(el.__t);
    el.__t = setTimeout(function () {
      el.textContent = message;
    }, 1500);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  }

  window.CartRewardsCore = {
    version: '1.1.0-supawear',
    debug: DEBUG,
    log: log,
    warn: warn,
    isDesignMode: isDesignMode,
    q: q,
    qFirst: qFirst,
    num: num,
    clamp: clamp,
    esc: esc,
    money: money,
    thresholds: thresholds,
    bus: bus,
    createMounts: createMounts,
    isCartPage: isCartPage,
    renderThemeCart: renderThemeCart,
    track: track,
    mergeTags: mergeTags,
    isGiftLine: isGiftLine,
    appliedRate: appliedRate,
    liveAnnounce: liveAnnounce,
    onReady: onReady
  };

  /* Always read the real cart, including in the theme editor. Writes are
     blocked in post(); reads never were the problem. */
  onReady(function () {
    bus.refresh();
  });
})();
